/* Fixed-tablet face-scan attendance kiosk.
 *
 * Route: /kiosk  (public — meant for a tablet mounted at the office entrance).
 * All heavy lifting (face detection + embedding) happens locally via
 * face-api.js. Server only stores the 128-float embedding + attendance rows.
 * NO raw photos are persisted anywhere.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import * as faceapi from "face-api.js";
import { http, notify } from "../api";

// Public CDN for the tiny face-detector + landmark + recognition models.
const MODEL_URL = "https://justadudewhohacks.github.io/face-api.js/models";
// Distance cutoffs (Euclidean over 128-dim face-api descriptors):
//   • face-api's own documented "same person" cutoff is 0.6.
//   • Real cross-device / cross-lighting scans for the SAME person land in 0.30–0.58.
//   • Strangers typically score 0.65–1.00.
// We use 0.55 — stricter than the model's own recommendation, but wide enough
// to accept a genuine employee whose scan lighting differs from enrolment.
const MATCH_THRESHOLD = 0.55;
// Second-line defence: even under the threshold, top-2 employees must differ
// by at least this margin. Without this, a multi-employee registry could
// silently accept the "closest" wrong person.
const MATCH_MARGIN    = 0.08;

// -----------------------------------------------------------------------------
// Kiosk authentication token.
// Precedence: URL ?token=... (one-time bootstrap) > localStorage (persisted).
// The token is set on the server via KIOSK_TOKEN in backend/.env; on the tablet
// it just needs to be entered ONCE — after that it survives PWA re-launches.
// Without a valid token, /hr/face/registry returns 401 and the kiosk shows
// "No employees have registered their face yet" even when embeddings ARE saved.
// -----------------------------------------------------------------------------
const LS_KIOSK_TOKEN_KEY = "rx_kiosk_token";
function _readKioskToken() {
  try {
    const url = new URLSearchParams(window.location.search).get("token");
    if (url) {
      window.localStorage.setItem(LS_KIOSK_TOKEN_KEY, url);
      // Strip ?token=... from the URL so the token isn't visible or shared.
      const clean = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", clean);
      return url;
    }
    return window.localStorage.getItem(LS_KIOSK_TOKEN_KEY) || "";
  } catch { return ""; }
}
let KIOSK_TOKEN = _readKioskToken();
let kioskHeaders = KIOSK_TOKEN ? { headers: { "X-Kiosk-Token": KIOSK_TOKEN } } : {};
export function setKioskToken(token) {
  KIOSK_TOKEN = (token || "").trim();
  kioskHeaders = KIOSK_TOKEN ? { headers: { "X-Kiosk-Token": KIOSK_TOKEN } } : {};
  try {
    if (KIOSK_TOKEN) window.localStorage.setItem(LS_KIOSK_TOKEN_KEY, KIOSK_TOKEN);
    else window.localStorage.removeItem(LS_KIOSK_TOKEN_KEY);
  } catch {}
}

let modelsLoadingPromise = null;
async function ensureModelsLoaded() {
  if (!modelsLoadingPromise) {
    modelsLoadingPromise = Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
    ]);
  }
  return modelsLoadingPromise;
}

// Tuned for Android tablets (Feb 2026). Front-facing tablet cameras are
// typically 720p or lower with uneven indoor lighting; TinyFaceDetector's
// default 0.5 threshold rejected valid faces intermittently. Lower threshold
// + larger input size + a warm-up wait fixes reliability without hurting
// desktop accuracy. Locked-match security is unchanged (see MATCH_THRESHOLD).
const TINY_DETECTOR_OPTS = { inputSize: 416, scoreThreshold: 0.3 };

async function detectFaceOnce(videoEl) {
  const options = new faceapi.TinyFaceDetectorOptions(TINY_DETECTOR_OPTS);
  const detection = await faceapi
    .detectSingleFace(videoEl, options)
    .withFaceLandmarks()
    .withFaceDescriptor();
  return detection || null;
}

// Return ALL detected faces with descriptors — used to enforce single-face policy.
async function detectAllFaces(videoEl) {
  const options = new faceapi.TinyFaceDetectorOptions(TINY_DETECTOR_OPTS);
  const dets = await faceapi
    .detectAllFaces(videoEl, options)
    .withFaceLandmarks()
    .withFaceDescriptors();
  return dets || [];
}

function euclid(a, b) {
  let s = 0;
  for (let i = 0; i < a.length; i++) { const d = a[i] - b[i]; s += d * d; }
  return Math.sqrt(s);
}

// Compute top-1 and top-2 matches against the registry.
// Returns { best, bestDist, second, secondDist } (any may be null).
function matchAgainstRegistry(embedding, registry) {
  let best = null, bestDist = Infinity, second = null, secondDist = Infinity;
  for (const emp of registry || []) {
    if (!emp.embedding || emp.embedding.length < 32) continue;
    const d = euclid(embedding, emp.embedding);
    if (d < bestDist) {
      second = best; secondDist = bestDist;
      best = emp; bestDist = d;
    } else if (d < secondDist) {
      second = emp; secondDist = d;
    }
  }
  return { best, bestDist, second, secondDist };
}

// Acceptance: within threshold AND clearly better than the second-best.
function isConfidentMatch(bestDist, secondDist) {
  if (bestDist > MATCH_THRESHOLD) return false;
  if (Number.isFinite(secondDist) && (secondDist - bestDist) < MATCH_MARGIN) return false;
  return true;
}

// Human-readable reason for the console — helps HR debug rejects.
function matchReason(bestDist, secondDist) {
  if (bestDist > MATCH_THRESHOLD) return `over threshold (${bestDist.toFixed(3)} > ${MATCH_THRESHOLD})`;
  if (Number.isFinite(secondDist) && (secondDist - bestDist) < MATCH_MARGIN)
    return `too close to runner-up (gap ${(secondDist - bestDist).toFixed(3)} < ${MATCH_MARGIN})`;
  return "accepted";
}

const MATCH_COOLDOWN_MS = 15000;  // don't re-scan the same person for 15s
const STABLE_HOLD_MS   = 2500;    // face must be detected+matched for this long before firing

/* ============================================================== */
/* AUTO-KIOSK: permanent fullscreen attendance camera.              */
/*   Continuously detects faces → picks best match in registry →   */
/*   POSTs /hr/face/attendance action=auto (server decides         */
/*   login vs logout) → shows success card ~3.5s → back to camera. */
/*   Hidden gesture: 5 quick taps opens the admin controls.        */
/* ============================================================== */
function KioskTokenPrompt({ onSaved }) {
  const [tok, setTok] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    const t = tok.trim();
    if (!t) { setErr("Enter a kiosk token"); return; }
    setBusy(true); setErr("");
    try {
      // Verify the token before persisting so the operator gets instant feedback.
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/hr/face/registry`,
        { headers: { "X-Kiosk-Token": t } },
      );
      if (!r.ok) throw new Error(r.status === 401 ? "Invalid token" : `HTTP ${r.status}`);
      onSaved(t);
    } catch (e) { setErr(e.message || "Could not validate token"); }
    finally { setBusy(false); }
  };
  return (
    <div
      data-testid="kiosk-token-prompt"
      style={{ position: "fixed", inset: 0, background: "#0f172a", color: "#f8fafc",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
    >
      <div style={{ maxWidth: 460, width: "100%", background: "#1e293b", padding: 26, borderRadius: 16 }}>
        <p style={{ margin: 0, color: "#94a3b8", fontSize: 12, letterSpacing: 2 }}>RUPEE XPERT · KIOSK</p>
        <h2 style={{ margin: "8px 0 6px" }}>Enter Kiosk Token</h2>
        <p style={{ color: "#cbd5e1", marginTop: 0, fontSize: 14 }}>
          The tablet needs to be authorised once before it can load registered
          employees or log attendance. Ask your HR admin for the token
          configured in <code>backend/.env</code> as <code>KIOSK_TOKEN</code>.
          The token is stored on this tablet only.
        </p>
        <input
          type="password"
          value={tok}
          onChange={(e) => setTok(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="Paste kiosk token"
          data-testid="kiosk-token-input"
          style={{ width: "100%", padding: 12, fontSize: 16, borderRadius: 8, border: "1px solid #334155",
                   background: "#0f172a", color: "#fff", marginTop: 8 }}
        />
        {err && (
          <div style={{ color: "#f87171", marginTop: 8, fontSize: 13 }} data-testid="kiosk-token-error">{err}</div>
        )}
        <button
          onClick={submit}
          disabled={busy}
          data-testid="kiosk-token-save"
          style={{ marginTop: 14, width: "100%", padding: 12, fontSize: 16, fontWeight: 600,
                   borderRadius: 8, border: "none", cursor: "pointer",
                   background: busy ? "#475569" : "#22c55e", color: "#0f172a" }}
        >{busy ? "Verifying…" : "Save & continue"}</button>
      </div>
    </div>
  );
}


function AutoKiosk({ registry, reloadRegistry, onOpenAdmin }) {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const loopRef = useRef(null);
  const busyRef = useRef(false);          // true while a match is being POSTed / shown
  const cooldownRef = useRef(new Map());  // user_id -> ts of last successful mark

  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState("Starting camera…");
  const [success, setSuccess] = useState(null);   // { name, employee_code, action, time, late, hours, duplicate }

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        await ensureModelsLoaded();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: 1280, height: 720 }, audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
        setReady(true); setStatus("Please look at the camera");
        runOverlay();
        runDetectionLoop();
      } catch (e) {
        setStatus("Camera permission denied — please allow camera access and refresh.");
      }
    };
    start();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (loopRef.current) clearTimeout(loopRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runOverlay = () => {
    const video = videoRef.current, canvas = overlayRef.current;
    if (!video || !canvas) return;
    const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.3 });
    const tick = async () => {
      if (!streamRef.current) return;
      try {
        const det = await faceapi.detectSingleFace(video, opts);
        const w = video.clientWidth || video.videoWidth;
        const h = video.clientHeight || video.videoHeight;
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, w, h);
        if (det && det.box) {
          const r = faceapi.resizeResults(det, { width: w, height: h }).box;
          const x = w - r.x - r.width;
          ctx.lineWidth = 4; ctx.strokeStyle = "#22c55e";
          ctx.shadowColor = "rgba(34,197,94,0.6)"; ctx.shadowBlur = 14;
          ctx.strokeRect(x, r.y, r.width, r.height);
          ctx.shadowBlur = 0;
        }
      } catch { /* ignore */ }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const runDetectionLoop = () => {
    const loop = async () => {
      if (!streamRef.current) return;
      if (!busyRef.current) {
        try {
          const dets = await detectAllFaces(videoRef.current);

          // No face — reset stability, idle prompt.
          if (!dets.length) {
            stableRef.current = { id: null, since: 0 };
            setStatus("Please look at the camera");
          }
          // More than one face — refuse and message the user.
          else if (dets.length > 1) {
            stableRef.current = { id: null, since: 0 };
            setStatus("Only one person should be in front of the camera.");
          }
          // Exactly one face — try to match strictly.
          else if (registry && registry.length) {
            const embed = Array.from(dets[0].descriptor);
            const { best, bestDist, second, secondDist } = matchAgainstRegistry(embed, registry);
            // eslint-disable-next-line no-console
            console.log("[kiosk] best=", best?.name, "dist=", bestDist?.toFixed(3),
                        "second=", second?.name, "secondDist=", secondDist === Infinity ? "-" : secondDist.toFixed(3),
                        "verdict=", matchReason(bestDist, secondDist));

            if (!best || !isConfidentMatch(bestDist, secondDist)) {
              stableRef.current = { id: null, since: 0 };
              setStatus("Face not recognised. Please try again or contact HR.");
            } else {
              const lastTs = cooldownRef.current.get(best.id) || 0;
              if (Date.now() - lastTs < MATCH_COOLDOWN_MS) {
                stableRef.current = { id: null, since: 0 };
              } else {
                const s = stableRef.current;
                if (s.id !== best.id) {
                  stableRef.current = { id: best.id, since: Date.now() };
                  setStatus("Face detected — please hold still…");
                } else {
                  const held = Date.now() - s.since;
                  if (held >= STABLE_HOLD_MS) {
                    stableRef.current = { id: null, since: 0 };
                    await markAttendance(best, bestDist);
                  } else {
                    setStatus(`Face detected — please hold still… (${Math.ceil((STABLE_HOLD_MS - held) / 1000)}s)`);
                  }
                }
              }
            }
          } else {
            setStatus("Please look at the camera");
          }
        } catch { /* ignore transient */ }
      }
      loopRef.current = setTimeout(loop, 500);
    };
    loopRef.current = setTimeout(loop, 500);
  };

  const markAttendance = async (best, bestDist) => {
    busyRef.current = true;
    setStatus(`Verifying ${best.name}…`);
    try {
      const r = await http.post("/hr/face/attendance", {
        user_id: best.id, action: "auto", similarity: bestDist, consent: true,
      }, kioskHeaders);
      const d = r.data || {};
      const emp = d.employee || best;
      const act = d.action === "logout" ? "logout" : d.action === "already_done" ? "already_done" : "login";
      const timeIso = act === "logout" ? d.logout_time : d.login_time;
      setSuccess({
        name: emp.name, employee_code: emp.employee_code || "",
        action: act,
        time: (timeIso || "").slice(11, 16),
        late: !!d.late_login,
        hours: d.working_hours,
        duplicate: !!d.duplicate,
      });
      cooldownRef.current.set(best.id, Date.now());
    } catch (e) {
      setStatus(e?.response?.data?.detail || "Could not record attendance");
    }
    // Show success ~3.5s then return to live camera.
    setTimeout(() => {
      setSuccess(null);
      setStatus("Please look at the camera");
      busyRef.current = false;
    }, 3500);
  };

  // Hidden gesture: 5 taps within 2 seconds → open admin.
  const gestureCountRef = useRef(0);
  const gestureTimerRef = useRef(null);
  const stableRef = useRef({ id: null, since: 0 });  // stability tracker for slow-fire
  const handleGestureTap = () => {
    gestureCountRef.current += 1;
    if (gestureTimerRef.current) clearTimeout(gestureTimerRef.current);
    gestureTimerRef.current = setTimeout(() => { gestureCountRef.current = 0; }, 2000);
    if (gestureCountRef.current >= 5) {
      gestureCountRef.current = 0;
      onOpenAdmin();
    }
  };

  const noRegistry = ready && (!registry || registry.length === 0);

  return (
    <div
      data-testid="kiosk-auto"
      style={{
        position: "fixed", inset: 0, background: "#000", color: "#f8fafc",
        overflow: "hidden", fontFamily: "'Inter', system-ui",
      }}
    >
      <video
        ref={videoRef} playsInline muted
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          objectFit: "cover", transform: "scaleX(-1)", background: "#000",
        }}
        data-testid="kiosk-auto-video"
      />
      <canvas
        ref={overlayRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      />
      {/* Transparent click-catcher for the hidden gesture (below success overlay, above video) */}
      <div
        onClick={handleGestureTap}
        data-testid="kiosk-auto-gesture"
        style={{ position: "absolute", inset: 0, cursor: "default", background: "transparent" }}
      />

      {/* Center face-outline guide */}
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
        <svg width="70%" height="70%" viewBox="0 0 200 260" style={{ maxWidth: 460, opacity: 0.55 }}>
          <defs>
            <linearGradient id="autoG" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#4ade80" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
          </defs>
          <ellipse cx="100" cy="130" rx="70" ry="95" fill="none" stroke="url(#autoG)" strokeWidth="3" strokeDasharray="6 8" />
        </svg>
      </div>

      {/* Header */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, padding: "16px 20px",
        background: "linear-gradient(180deg, rgba(0,0,0,0.55), transparent)",
        display: "flex", justifyContent: "space-between", alignItems: "center", pointerEvents: "none",
      }}>
        <div>
          <p style={{ margin: 0, letterSpacing: 2, fontSize: 11, color: "#e2e8f0" }}>RUPEE XPERT</p>
          <h2 style={{ margin: 0, fontSize: 22 }}>Face Attendance</h2>
        </div>
        <div style={{ textAlign: "right", color: "#e2e8f0" }}>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{new Date().toLocaleTimeString("en-IN", { hour12: false })}</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</div>
        </div>
      </div>

      {/* Bottom status / instruction */}
      {!success && (
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, padding: "20px 24px 28px",
          background: "linear-gradient(0deg, rgba(0,0,0,0.75), transparent)",
          textAlign: "center", pointerEvents: "none",
        }}>
          <div style={{ fontSize: 28, fontWeight: 700 }}>Please look at the camera</div>
          <div style={{ marginTop: 6, fontSize: 15, color: "#cbd5e1" }} data-testid="kiosk-auto-status">{status}</div>
          {noRegistry && (
            <div style={{ marginTop: 8, color: "#fde68a" }}>
              No employees have registered their face yet. Tap the screen 5 times quickly to open admin.
            </div>
          )}
        </div>
      )}

      {/* Success overlay (auto-dismisses) */}
      {success && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(6,95,70,0.85)", pointerEvents: "none",
        }} data-testid="kiosk-auto-success">
          <div style={{ textAlign: "center", padding: 30 }}>
            <div style={{ fontSize: 26, marginBottom: 8, color: "#a7f3d0" }}>
              {success.action === "already_done" ? "Already checked in & out today" : "Attendance Marked"}
            </div>
            <div style={{ fontSize: 48, fontWeight: 800 }}>{success.name}</div>
            {success.employee_code && (
              <div style={{ fontSize: 14, color: "#a7f3d0", marginTop: 4 }}>{success.employee_code}</div>
            )}
            {success.time && (
              <div style={{ marginTop: 18, fontSize: 22 }}>
                {success.action === "logout" ? "Logout" : "Login"}:{" "}
                <strong>{success.time}</strong>
              </div>
            )}
            {success.action === "login" && success.late && (
              <div style={{ marginTop: 8, color: "#fecaca", fontWeight: 700 }}>⚠ Late login</div>
            )}
            {success.action === "logout" && success.hours != null && (
              <div style={{ marginTop: 8, color: "#a7f3d0" }}>Working hours: <strong>{success.hours}h</strong></div>
            )}
            {success.duplicate && success.action !== "already_done" && (
              <div style={{ marginTop: 8, color: "#fde68a" }}>Already recorded — no change.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================== */
/* Full-screen scanning view — reused for Login, Logout, HR enroll  */
/* ============================================================== */
function FullscreenScan({ videoRef, overlayRef, message, headline, onCancel }) {
  return (
    <div
      data-testid="fullscreen-scan"
      style={{
        position: "fixed", inset: 0, zIndex: 200, background: "#000",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}
    >
      {/* Video fills the entire screen */}
      <video
        ref={videoRef}
        playsInline
        muted
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          objectFit: "cover", transform: "scaleX(-1)", background: "#000",
        }}
        data-testid="kiosk-video"
      />
      {/* Detection overlay */}
      <canvas
        ref={overlayRef}
        data-testid="kiosk-overlay"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      />
      {/* Center face-outline guide (SVG) */}
      <div style={{
        position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
        pointerEvents: "none",
      }}>
        <svg width="72%" height="72%" viewBox="0 0 200 260" style={{ maxWidth: 460, opacity: 0.55 }}>
          <defs>
            <linearGradient id="scanG" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#4ade80" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
          </defs>
          <ellipse cx="100" cy="130" rx="70" ry="95"
                   fill="none" stroke="url(#scanG)" strokeWidth="3"
                   strokeDasharray="6 8" />
        </svg>
      </div>
      {/* Top header — headline + cancel */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, padding: "16px 20px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        background: "linear-gradient(180deg, rgba(0,0,0,0.6), transparent)",
        color: "#fff",
      }}>
        <div>
          <p style={{ margin: 0, letterSpacing: 2, fontSize: 11, color: "#e2e8f0" }}>RUPEE XPERT</p>
          <h2 style={{ margin: 0, fontSize: 22 }}>{headline}</h2>
        </div>
        <button
          onClick={onCancel}
          data-testid="fullscreen-scan-cancel"
          style={{
            background: "rgba(15,23,42,0.75)", color: "#fff",
            border: "1px solid rgba(148,163,184,0.6)", borderRadius: 10,
            padding: "10px 16px", fontSize: 15, fontWeight: 600, cursor: "pointer",
          }}
        >← Cancel</button>
      </div>
      {/* Bottom instruction + status */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, padding: "20px 24px 28px",
        background: "linear-gradient(0deg, rgba(0,0,0,0.75), transparent)",
        color: "#fff", textAlign: "center",
      }}>
        <div style={{ fontSize: 26, fontWeight: 700 }}>Look at the camera</div>
        <div style={{ marginTop: 6, fontSize: 15, color: "#cbd5e1" }} data-testid="fullscreen-scan-status">
          {message || "Position your face inside the outline"}
        </div>
      </div>
    </div>
  );
}

/* ============================================================== */
export default function AttendanceKiosk() {
  const [mode, setMode] = useState("auto"); // 'auto' = permanent kiosk, 'admin' = HR controls
  const [phase, setPhase] = useState("idle"); // idle | loading | scanning | verifying | done | error
  const [action, setAction] = useState("login");
  const [registry, setRegistry] = useState(null);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualEmployees, setManualEmployees] = useState([]);
  const [consent, setConsent] = useState(true);
  const [enrollOpen, setEnrollOpen] = useState(false);

  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const scanAbort = useRef(false);
  const rafRef = useRef(null);

  const [tokenError, setTokenError] = useState(false);

  const canScan = registry && registry.length > 0;

  // Load registry once. If we get 401 (missing / wrong kiosk token) surface
  // a clear "Enter Kiosk Token" prompt instead of the misleading empty-list
  // message. Any other failure just leaves the registry empty as before.
  const loadRegistry = () => {
    setTokenError(false);
    return http.get("/hr/face/registry", kioskHeaders)
      .then((r) => setRegistry(r.data.employees || []))
      .catch((e) => {
        setRegistry([]);
        if (e?.response?.status === 401) setTokenError(true);
      });
  };
  useEffect(() => { loadRegistry(); /* eslint-disable-next-line */ }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // Ask for HD but fall back gracefully — many Android tablets max out
        // at 720p on the front camera.
        video: {
          facingMode: "user",
          width:  { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      return true;
    } catch (e) {
      setMessage("Camera permission denied. Please allow camera access.");
      setPhase("error");
      return false;
    }
  };

  const stopCamera = () => {
    scanAbort.current = true;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    const canvas = overlayRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx && ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // Continuously detect + draw a green outline while the camera is running.
  const runOverlay = () => {
    const video = videoRef.current;
    const canvas = overlayRef.current;
    if (!video || !canvas || !streamRef.current) return;
    const detOpts = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.3 });
    const tick = async () => {
      if (!streamRef.current || scanAbort.current) return;
      try {
        const det = await faceapi.detectSingleFace(video, detOpts);
        // Match canvas to displayed video size so coordinates align.
        const w = video.clientWidth || video.videoWidth;
        const h = video.clientHeight || video.videoHeight;
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, w, h);
        if (det && det.box) {
          const resized = faceapi.resizeResults(det, { width: w, height: h });
          const b = resized.box;
          // The <video> is mirrored (scaleX(-1)), so mirror the drawn box too.
          const x = w - b.x - b.width;
          ctx.lineWidth = 4;
          ctx.strokeStyle = "#22c55e";
          ctx.shadowColor = "rgba(34,197,94,0.6)";
          ctx.shadowBlur = 14;
          ctx.strokeRect(x, b.y, b.width, b.height);
          ctx.shadowBlur = 0;
          // Corner markers
          const c = 18;
          ctx.strokeStyle = "#4ade80";
          ctx.lineWidth = 6;
          [[x, b.y, 1, 1], [x + b.width, b.y, -1, 1], [x, b.y + b.height, 1, -1], [x + b.width, b.y + b.height, -1, -1]]
            .forEach(([cx, cy, sx, sy]) => {
              ctx.beginPath();
              ctx.moveTo(cx, cy + sy * c); ctx.lineTo(cx, cy); ctx.lineTo(cx + sx * c, cy);
              ctx.stroke();
            });
        }
      } catch { /* ignore transient */ }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => () => stopCamera(), []);

  const scan = async (which) => {
    if (!consent) { notify.error("Please accept the biometric consent to proceed"); return; }
    setAction(which);
    setResult(null);
    setPhase("loading");
    setMessage("Preparing camera…");
    await ensureModelsLoaded();
    const ok = await startCamera();
    if (!ok) return;
    setPhase("scanning");
    setMessage("Look at the camera…");
    scanAbort.current = false;
    runOverlay();
    // Try up to 12 seconds for first detection — must be exactly one face.
    let dets = [];
    const start = Date.now();
    while (!scanAbort.current && Date.now() - start < 12000) {
      dets = await detectAllFaces(videoRef.current);
      if (dets.length === 1) break;
      if (dets.length > 1) setMessage("Only one person should be in front of the camera.");
      await new Promise((r) => setTimeout(r, 250));
    }
    if (dets.length !== 1) {
      setPhase("error");
      setMessage(dets.length > 1
        ? "Multiple faces detected — only one person should be in front of the camera."
        : "No face detected. Please try again.");
      stopCamera(); return;
    }
    let detection = dets[0];

    // Stability hold — require ~2s of stable face before recognition, refuse if >1 face appears
    setMessage("Face detected — please hold still…");
    const holdMs = 2000;
    const holdStart = Date.now();
    while (!scanAbort.current && Date.now() - holdStart < holdMs) {
      const remaining = Math.ceil((holdMs - (Date.now() - holdStart)) / 1000);
      setMessage(`Face detected — hold still… (${remaining}s)`);
      const d2 = await detectAllFaces(videoRef.current);
      if (d2.length > 1) {
        setPhase("error"); setMessage("Only one person should be in front of the camera.");
        stopCamera(); return;
      }
      if (d2.length === 1) detection = d2[0];
      await new Promise((r) => setTimeout(r, 350));
    }
    if (scanAbort.current) { stopCamera(); return; }

    // Strict match: threshold + margin between top-1 and top-2.
    setPhase("verifying"); setMessage("Verifying identity…");
    const embed = Array.from(detection.descriptor);
    const { best, bestDist, second, secondDist } = matchAgainstRegistry(embed, registry || []);
    // eslint-disable-next-line no-console
    console.log("[kiosk-scan] best=", best?.name, "dist=", bestDist?.toFixed(3),
                "second=", second?.name, "secondDist=", secondDist === Infinity ? "-" : secondDist.toFixed(3),
                "verdict=", matchReason(bestDist, secondDist));
    stopCamera();

    if (!best || !isConfidentMatch(bestDist, secondDist)) {
      setPhase("error");
      setMessage("Face not recognised. Please try again or contact HR.");
      return;
    }

    // Mark attendance.
    try {
      const r = await http.post("/hr/face/attendance", {
        user_id: best.id, action: which, similarity: bestDist, consent: true,
      }, kioskHeaders);
      setResult({ ...r.data, employee: r.data.employee || best });
      setPhase("done");
      setMessage("");
    } catch (e) {
      setPhase("error");
      setMessage(e?.response?.data?.detail || "Could not save attendance");
    }
  };

  const openManual = async () => {
    if (!manualEmployees.length) {
      try {
        const r = await http.get("/hr/face/registry", kioskHeaders); // returns registered users
        // Also fetch full list for fallback (HR-only), best-effort:
        const full = await http.get("/hr/employees", kioskHeaders).catch(() => null);
        setManualEmployees(full ? full.data.employees.filter((e) => e.active) : r.data.employees);
      } catch { setManualEmployees([]); }
    }
    setManualOpen(true);
  };

  const manualMark = async (empId, which) => {
    try {
      const r = await http.post("/hr/face/attendance", {
        user_id: empId, action: which, similarity: null, consent: true,
      }, kioskHeaders);
      setResult({ ...r.data });
      setPhase("done"); setManualOpen(false); setAction(which);
    } catch (e) { notify.error(e, "Could not mark attendance"); }
  };

  const reset = () => { setResult(null); setPhase("idle"); setMessage(""); stopCamera(); };
  const cancelScan = () => { stopCamera(); setPhase("idle"); setMessage(""); };

  const scanning = phase === "loading" || phase === "scanning" || phase === "verifying";

  // -------- Permanent full-screen face-attendance kiosk --------
  if (mode === "auto") {
    if (tokenError) {
      return (
        <KioskTokenPrompt onSaved={(t) => { setKioskToken(t); loadRegistry(); }} />
      );
    }
    return (
      <AutoKiosk
        registry={registry}
        reloadRegistry={loadRegistry}
        onOpenAdmin={() => setMode("admin")}
      />
    );
  }

  // -------- Admin/HR controls screen (revealed by hidden gesture) --------
  return (
    <div className="kiosk-shell" data-testid="attendance-kiosk"
      style={{ minHeight: "100vh", background: "#0f172a", color: "#f8fafc", padding: 24, fontFamily: "'Inter', system-ui" }}>
      {/* Full-screen scanning overlay (Login / Logout) — reuses same refs + logic */}
      {scanning && (
        <FullscreenScan
          videoRef={videoRef}
          overlayRef={overlayRef}
          message={message}
          headline={
            phase === "verifying" ? "Verifying identity…" :
            action === "logout"   ? "Scanning face — Logout"  :
                                    "Scanning face — Login"
          }
          onCancel={cancelScan}
        />
      )}
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <p className="eyebrow" style={{ margin: 0, color: "#94a3b8" }}>RUPEE XPERT · KIOSK</p>
          <h1 style={{ margin: 0, fontSize: 28 }}>Attendance</h1>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button
            className="outline-button compact"
            onClick={() => setMode("auto")}
            data-testid="kiosk-return-to-auto"
            style={{ color: "#fff", borderColor: "#22c55e" }}
          >← Return to Kiosk</button>
          <button
            className="outline-button compact"
            onClick={() => {
              const el = document.documentElement;
              if (document.fullscreenElement) document.exitFullscreen();
              else if (el.requestFullscreen) el.requestFullscreen();
              else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
            }}
            data-testid="kiosk-fullscreen"
            style={{ color: "#fff", borderColor: "#475569" }}
          >⛶ Fullscreen</button>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{new Date().toLocaleTimeString("en-IN", { hour12: false })}</div>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</div>
          </div>
        </div>
      </header>

      {phase === "done" && result ? (
        <div style={{ background: "#065f46", padding: 32, borderRadius: 16, textAlign: "center" }} data-testid="kiosk-success">
          <div style={{ fontSize: 22, marginBottom: 8 }}>Attendance Marked Successfully</div>
          <div style={{ fontSize: 36, fontWeight: 800 }}>{result.employee?.name || "—"}</div>
          <div style={{ fontSize: 14, color: "#a7f3d0", marginTop: 4 }}>{result.employee?.employee_code || ""}</div>
          <div style={{ marginTop: 20, fontSize: 18 }}>
            {action === "login" ? "Login time" : "Logout time"}:{" "}
            <strong>{((action === "login" ? result.login_time : result.logout_time) || "").slice(11, 16)}</strong>
          </div>
          {action === "login" && result.late_login && (
            <div style={{ marginTop: 8, color: "#fecaca", fontWeight: 700 }}>⚠ Late login</div>
          )}
          {action === "logout" && result.working_hours != null && (
            <div style={{ marginTop: 8, color: "#a7f3d0" }}>Working hours today: <strong>{result.working_hours}h</strong></div>
          )}
          {result.duplicate && (
            <div style={{ marginTop: 8, color: "#fde68a" }}>Already recorded for today — no change.</div>
          )}
          <button data-testid="kiosk-done" className="primary-button" style={{ marginTop: 24 }} onClick={reset}>Done</button>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr", alignItems: "stretch" }}>
          <div style={{ background: "#1e293b", padding: 20, borderRadius: 16, display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
            <div style={{
              width: "100%", maxWidth: 480, aspectRatio: "4/3",
              borderRadius: 12, background: "#000",
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "1px dashed #334155", color: "#64748b", fontSize: 14, textAlign: "center", padding: 12,
            }} data-testid="kiosk-video-placeholder">
              Tap <strong style={{ color: "#e2e8f0", margin: "0 4px" }}>Mark Attendance (Login)</strong> or <strong style={{ color: "#e2e8f0", margin: "0 4px" }}>Check Out (Logout)</strong> to open camera
            </div>
            <div style={{ minHeight: 28, color: "#cbd5e1" }} data-testid="kiosk-status">{message}</div>
            <label style={{ fontSize: 12, color: "#94a3b8", display: "flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="kiosk-consent" />
              I consent to face-based biometric attendance (see privacy notice below).
            </label>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <button
              className="primary-button"
              onClick={() => scan("login")}
              disabled={!canScan || phase === "scanning" || phase === "verifying" || phase === "loading" || !consent}
              data-testid="kiosk-login-button"
              style={{ padding: "26px 20px", fontSize: 22 }}
            >Mark Attendance (Login)</button>
            <button
              className="outline-button"
              onClick={() => scan("logout")}
              disabled={!canScan || phase === "scanning" || phase === "verifying" || phase === "loading" || !consent}
              data-testid="kiosk-logout-button"
              style={{ padding: "26px 20px", fontSize: 22, color: "#fff", borderColor: "#94a3b8" }}
            >Check Out (Logout)</button>
            <button
              className="outline-button"
              onClick={openManual}
              data-testid="kiosk-manual"
              style={{ padding: "14px 20px", color: "#fff", borderColor: "#475569" }}
            >Manual attendance (face not available)</button>
            <button
              className="outline-button"
              onClick={() => setEnrollOpen(true)}
              data-testid="kiosk-enroll"
              style={{ padding: "14px 20px", color: "#fff", borderColor: "#475569" }}
            >HR: Register / Update Face</button>

            {phase === "error" && (
              <div style={{ background: "#7f1d1d", padding: 14, borderRadius: 10 }} data-testid="kiosk-error">
                {message} <button className="outline-button compact" onClick={reset} style={{ marginLeft: 8, color: "#fff" }}>Try again</button>
              </div>
            )}

            <div style={{ marginTop: 8, background: "#0b1220", padding: 14, borderRadius: 10, fontSize: 12, color: "#94a3b8", lineHeight: 1.5 }}>
              <strong style={{ color: "#e2e8f0" }}>Privacy notice.</strong> This tablet captures a mathematical face template
              (embedding) only and matches it to your registered profile. No photos are stored on the server. Attendance
              records are shared only with HR and management. Contact HR to withdraw consent or delete your face profile.
            </div>

            {registry && registry.length === 0 && (
              <div style={{ background: "#78350f", padding: 14, borderRadius: 10, fontSize: 13 }}>
                No employees have registered their face yet. Ask HR to open <strong>HR → Employees → Edit → Register Face</strong>.
              </div>
            )}
          </div>
        </div>
      )}

      {manualOpen && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(2,6,23,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 24, zIndex: 50,
        }} onClick={() => setManualOpen(false)}>
          <div style={{ background: "#0f172a", padding: 20, borderRadius: 16, maxWidth: 720, width: "100%", maxHeight: "90vh", overflow: "auto" }}
               onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Manual attendance</h3>
            <p className="muted" style={{ color: "#94a3b8" }}>Choose employee, then Login or Logout.</p>
            <div style={{ display: "grid", gap: 8 }}>
              {manualEmployees.map((e) => (
                <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 10, borderRadius: 8, background: "#1e293b" }}>
                  <div><strong>{e.name}</strong><small style={{ color: "#94a3b8" }}> · {e.employee_code || e.email || e.designation || ""}</small></div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="primary-button compact" onClick={() => manualMark(e.id, "login")} data-testid={`kiosk-manual-login-${e.id}`}>Login</button>
                    <button className="outline-button compact" onClick={() => manualMark(e.id, "logout")} style={{ color: "#fff" }}>Logout</button>
                  </div>
                </div>
              ))}
              {manualEmployees.length === 0 && <div className="muted" style={{ color: "#94a3b8" }}>No active employees found.</div>}
            </div>
            <div style={{ textAlign: "right", marginTop: 12 }}>
              <button className="outline-button" onClick={() => setManualOpen(false)} style={{ color: "#fff" }}>Close</button>
            </div>
          </div>
        </div>
      )}
      {enrollOpen && (
        <KioskEnrollOverlay
          onClose={() => setEnrollOpen(false)}
          onDone={() => {
            setEnrollOpen(false);
            // Reload registry so newly-enrolled faces are matchable immediately.
            http.get("/hr/face/registry", kioskHeaders)
              .then((r) => setRegistry(r.data.employees || []))
              .catch(() => {});
          }}
        />
      )}
    </div>
  );
}


/* ============================================================== */
/* Kiosk-side HR enrollment overlay:                                */
/*   HR signs in on the tablet → picks employee → captures face.    */
/*   No new endpoints — reuses /auth/login, /hr/employees,         */
/*   /hr/face/register, /auth/logout.                              */
/* ============================================================== */
function KioskEnrollOverlay({ onClose, onDone }) {
  const [step, setStep] = useState("signin");   // signin | pick | capture | done
  const [creds, setCreds] = useState({ id: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);

  const signIn = async () => {
    setBusy(true); setError("");
    try {
      const body = creds.id.includes("@")
        ? { email: creds.id.trim(), password: creds.password }
        : { employee_code: creds.id.trim(), password: creds.password };
      const login = await http.post("/auth/login", body);
      if (!["admin", "sub_admin"].includes(login.data.role)) {
        await http.post("/auth/logout").catch(() => {});
        throw new Error("Only HR / Admin can enroll faces");
      }
      const list = await http.get("/hr/employees");
      setEmployees((list.data.employees || []).filter((e) => e.active));
      setStep("pick");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Sign-in failed");
    } finally { setBusy(false); }
  };

  const closeAll = async () => {
    try { await http.post("/auth/logout"); } catch {}
    onClose();
  };
  const finish = async () => {
    try { await http.post("/auth/logout"); } catch {}
    onDone();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(2,6,23,0.9)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, zIndex: 60 }}
         onClick={closeAll}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "#0f172a", padding: 20, borderRadius: 16, width: "100%", maxWidth: 560, color: "#f8fafc", maxHeight: "92vh", overflow: "auto" }}
           data-testid="kiosk-enroll-panel">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Register / Update Face</h3>
          <button className="outline-button compact" onClick={closeAll} style={{ color: "#fff" }}>Close</button>
        </div>

        {step === "signin" && (
          <div style={{ marginTop: 14 }}>
            <p className="muted" style={{ color: "#94a3b8" }}>Sign in with your HR/Admin account. You'll be logged out automatically when you finish.</p>
            <label style={{ display: "block", marginTop: 8 }}>Employee ID or email
              <input value={creds.id} onChange={(e) => setCreds({ ...creds, id: e.target.value })}
                     data-testid="kiosk-enroll-id"
                     style={{ display: "block", width: "100%", padding: 10, marginTop: 4, borderRadius: 8, border: "1px solid #334155", background: "#1e293b", color: "#fff" }} />
            </label>
            <label style={{ display: "block", marginTop: 8 }}>Password
              <input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                     data-testid="kiosk-enroll-password"
                     style={{ display: "block", width: "100%", padding: 10, marginTop: 4, borderRadius: 8, border: "1px solid #334155", background: "#1e293b", color: "#fff" }} />
            </label>
            {error && <div style={{ marginTop: 8, color: "#fca5a5" }}>{error}</div>}
            <div style={{ textAlign: "right", marginTop: 12 }}>
              <button className="primary-button" onClick={signIn} disabled={busy || !creds.id || !creds.password}
                      data-testid="kiosk-enroll-signin">
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </div>
          </div>
        )}

        {step === "pick" && (
          <div style={{ marginTop: 14 }}>
            <p className="muted" style={{ color: "#94a3b8" }}>Pick the employee whose face you want to register / update.</p>
            <div style={{ display: "grid", gap: 6, maxHeight: 320, overflow: "auto" }}>
              {employees.map((e) => (
                <button key={e.id}
                        onClick={() => { setSelected(e); setStep("capture"); }}
                        data-testid={`kiosk-enroll-pick-${e.id}`}
                        style={{ textAlign: "left", padding: 12, borderRadius: 10, background: "#1e293b", border: "1px solid #334155", color: "#fff", cursor: "pointer" }}>
                  <strong>{e.name}</strong>
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>
                    {e.employee_code || e.email} · {e.role}
                    {e.face_registered && <span style={{ color: "#4ade80" }}> · Face already registered</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "capture" && selected && (
          <div style={{ marginTop: 14 }}>
            <div style={{ marginBottom: 10, color: "#cbd5e1" }}>
              Registering: <strong>{selected.name}</strong> · {selected.employee_code || selected.email}
            </div>
            <InlineFaceCapture
              employee={selected}
              onCancel={() => setStep("pick")}
              onSaved={() => setStep("done")}
            />
          </div>
        )}

        {step === "done" && (
          <div style={{ marginTop: 14, textAlign: "center" }}>
            <div style={{ fontSize: 20, color: "#4ade80" }}>Face registered successfully</div>
            <div className="muted" style={{ color: "#94a3b8", marginTop: 6 }}>You can register another employee or finish.</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 14 }}>
              <button className="outline-button" onClick={() => setStep("pick")} style={{ color: "#fff" }}>Register another</button>
              <button className="primary-button" onClick={finish} data-testid="kiosk-enroll-finish">Finish</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InlineFaceCapture({ employee, onCancel, onSaved }) {
  const [phase, setPhase] = useState("idle");
  const [message, setMessage] = useState("");
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const stop = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };
  useEffect(() => () => stop(), []);

  const runOverlay = () => {
    const v = videoRef.current, c = overlayRef.current;
    if (!v || !c) return;
    const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.3 });
    const tick = async () => {
      if (!streamRef.current) return;
      try {
        const det = await faceapi.detectSingleFace(v, opts);
        const w = v.clientWidth || v.videoWidth, h = v.clientHeight || v.videoHeight;
        if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, w, h);
        if (det && det.box) {
          const r = faceapi.resizeResults(det, { width: w, height: h }).box;
          const x = w - r.x - r.width;
          ctx.lineWidth = 4; ctx.strokeStyle = "#22c55e";
          ctx.shadowColor = "rgba(34,197,94,0.6)"; ctx.shadowBlur = 14;
          ctx.strokeRect(x, r.y, r.width, r.height);
          ctx.shadowBlur = 0;
        }
      } catch {}
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const capture = async () => {
    setPhase("loading"); setMessage("Preparing camera…");
    await ensureModelsLoaded();
    try {
      // Ask for a decent resolution — some Android tablets default to 320×240
      // which is too small for reliable TinyFaceDetector output.
      const s = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width:  { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = s;
      if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play(); }
      runOverlay();
    } catch { setPhase("error"); setMessage("Camera permission denied"); return; }
    // Warm-up: let the video decoder produce real frames before we start
    // sniffing (first ~600 ms are often black on Android).
    await new Promise((r) => setTimeout(r, 700));
    setPhase("scanning"); setMessage("Look straight at the camera…");
    let det = null;
    // 1) Wait up to 15s for EXACTLY ONE face. Show a live hint if the detector
    //    is finding nothing so the user can adjust distance / angle / lighting.
    const t0 = Date.now();
    let noFaceTicks = 0;
    while (Date.now() - t0 < 15000) {
      const dets = await detectAllFaces(videoRef.current);
      if (dets.length === 1) { det = dets[0]; break; }
      if (dets.length > 1) { setMessage("Only one person should be in front of the camera."); noFaceTicks = 0; }
      else { noFaceTicks += 1; if (noFaceTicks > 3) setMessage("Move a little closer and face the camera…"); }
      await new Promise((r) => setTimeout(r, 250));
    }
    if (!det) {
      stop(); setPhase("error");
      setMessage("Could not detect your face. Move closer, remove glasses if reflective, and make sure your face is well-lit — then tap Start again.");
      return;
    }
    // 2) Face seen — ask to hold still, then wait ~2s of stable single-face presence.
    setMessage("Face detected — please hold still…");
    const holdMs = 2000;
    const holdStart = Date.now();
    while (Date.now() - holdStart < holdMs) {
      const remaining = Math.ceil((holdMs - (Date.now() - holdStart)) / 1000);
      setMessage(`Face detected — hold still… (${remaining}s)`);
      const dets2 = await detectAllFaces(videoRef.current);
      if (dets2.length > 1) {
        stop(); setPhase("error"); setMessage("Multiple faces detected — only one person should be in front of the camera.");
        return;
      }
      if (dets2.length === 1) det = dets2[0];   // keep the freshest descriptor
      await new Promise((r) => setTimeout(r, 350));
    }
    stop();
    try {
      await http.post("/hr/face/register", {
        user_id: employee.id, embedding: Array.from(det.descriptor), consent: true,
      });
      onSaved();
    } catch (e) {
      setPhase("error"); setMessage(e?.response?.data?.detail || "Could not save face");
    }
  };

  const scanning = phase === "loading" || phase === "scanning";

  return (
    <div>
      {scanning && (
        <FullscreenScan
          videoRef={videoRef}
          overlayRef={overlayRef}
          message={message}
          headline={`Registering face — ${employee.name}`}
          onCancel={() => { stop(); setPhase("idle"); setMessage(""); }}
        />
      )}
      <div style={{ minHeight: 20, marginTop: 8, color: "#cbd5e1" }} data-testid="kiosk-enroll-status">{message}</div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        <button className="outline-button" onClick={onCancel} style={{ color: "#fff" }}>Back</button>
        <button className="primary-button" onClick={capture} disabled={scanning}
                data-testid="kiosk-enroll-capture">
          {scanning ? "Working…" : "Capture & save"}
        </button>
      </div>
    </div>
  );
}
export function FaceRegisterModal({ employee, onClose, onSaved }) {
  const [phase, setPhase] = useState("idle");
  const [message, setMessage] = useState("");
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const stopCam = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };
  useEffect(() => () => stopCam(), []);

  const runOverlay = () => {
    const video = videoRef.current, canvas = overlayRef.current;
    if (!video || !canvas) return;
    const detOpts = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.3 });
    const tick = async () => {
      if (!streamRef.current) return;
      try {
        const det = await faceapi.detectSingleFace(video, detOpts);
        const w = video.clientWidth || video.videoWidth;
        const h = video.clientHeight || video.videoHeight;
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, w, h);
        if (det && det.box) {
          const r = faceapi.resizeResults(det, { width: w, height: h }).box;
          const x = w - r.x - r.width;
          ctx.lineWidth = 4; ctx.strokeStyle = "#22c55e";
          ctx.shadowColor = "rgba(34,197,94,0.6)"; ctx.shadowBlur = 14;
          ctx.strokeRect(x, r.y, r.width, r.height);
          ctx.shadowBlur = 0;
        }
      } catch {}
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const capture = async () => {
    setPhase("loading"); setMessage("Loading models & camera…");
    await ensureModelsLoaded();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width:  { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      runOverlay();
    } catch (e) {
      setPhase("error"); setMessage("Camera permission denied"); return;
    }
    setPhase("scanning"); setMessage("Look straight at the camera…");
    let detection = null;
    // 1) First-detection wait — must be exactly one face.
    const start = Date.now();
    while (Date.now() - start < 12000) {
      const dets = await detectAllFaces(videoRef.current);
      if (dets.length === 1) { detection = dets[0]; break; }
      if (dets.length > 1) setMessage("Only one person should be in front of the camera.");
      await new Promise((r) => setTimeout(r, 250));
    }
    if (!detection) { stopCam(); setPhase("error"); setMessage("Could not detect a single clear face — please try again with better lighting."); return; }
    // 2) Stability hold — face must remain ~2s before capture; refuse if >1 face.
    setMessage("Face detected — please hold still…");
    const holdMs = 2000;
    const holdStart = Date.now();
    while (Date.now() - holdStart < holdMs) {
      const remaining = Math.ceil((holdMs - (Date.now() - holdStart)) / 1000);
      setMessage(`Face detected — hold still… (${remaining}s)`);
      const dets2 = await detectAllFaces(videoRef.current);
      if (dets2.length > 1) {
        stopCam(); setPhase("error");
        setMessage("Multiple faces detected — only one person should be in front of the camera.");
        return;
      }
      if (dets2.length === 1) detection = dets2[0];
      await new Promise((r) => setTimeout(r, 350));
    }
    stopCam();
    try {
      await http.post("/hr/face/register", {
        user_id: employee.id,
        embedding: Array.from(detection.descriptor),
        consent: true,
      });
      setPhase("done"); setMessage("Face registered successfully");
      onSaved && onSaved();
    } catch (e) {
      setPhase("error"); setMessage(e?.response?.data?.detail || "Could not register face");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete registered face for ${employee.name}?`)) return;
    try {
      await http.delete(`/hr/face/register/${employee.id}`);
      onSaved && onSaved();
      onClose();
    } catch (e) { notify.error(e, "Could not delete"); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24, zIndex: 100,
    }}>
      <div style={{ background: "#fff", padding: 20, borderRadius: 12, width: "100%", maxWidth: 520 }} data-testid="face-register-modal">
        <h3 style={{ marginTop: 0 }}>Register face — {employee.name}</h3>
        <p className="muted">Only a mathematical face template is stored; no photos are saved on the server. Employee consent is assumed via HR onboarding.</p>
        <div style={{ position: "relative", width: "100%" }}>
          <video ref={videoRef} playsInline muted style={{ width: "100%", borderRadius: 8, background: "#000", aspectRatio: "4/3", transform: "scaleX(-1)", display: "block" }} />
          <canvas ref={overlayRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />
        </div>
        <div style={{ minHeight: 24, marginTop: 8 }} data-testid="face-register-status">{message}</div>
        <div className="form-actions" style={{ marginTop: 12 }}>
          <button className="outline-button" onClick={() => { stopCam(); onClose(); }}>Close</button>
          <button className="outline-button danger-button" onClick={remove}>Delete registered face</button>
          <button className="primary-button" onClick={capture} disabled={phase === "loading" || phase === "scanning"} data-testid="face-register-capture">
            {phase === "loading" || phase === "scanning" ? "Working…" : "Capture & save"}
          </button>
        </div>
      </div>
    </div>
  );
}
